import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CHAT_TURN_EVENT } from '../services/ChatService';

const STILL_WORKING_SECONDS = 60;
const FAILED_RED = '#dc2626';

/**
 * What Atom is doing while a chat answer is on its way, read from the turn's trace: each tool it calls, with the
 * FLEX Commands under it (collapsed to a count; a failed one opens by itself) and the one running now. Shown in place
 * of a plain "Processing" line by any chat screen; it follows the turn ChatService announces, so every way of sending
 * (typing, a button, a wizard form, files) gets it.
 */
const ChatTurnActivity = ({ colors = {}, accent = '#22c55e', compact = false, onGrow }) => {
  const [turn, setTurn] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState({});
  const listRef = useRef(null);
  const pinnedToBottom = useRef(true);

  useEffect(() => {
    const onTurn = (e) => setTurn((prev) => (prev && prev.turnId !== e.detail.turnId ? e.detail : { ...prev, ...e.detail }));
    window.addEventListener(CHAT_TURN_EVENT, onTurn);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      window.removeEventListener(CHAT_TURN_EVENT, onTurn);
      clearInterval(tick);
    };
  }, []);

  const steps = turn?.steps || [];
  const rowCount = countRows(steps);
  useEffect(() => {
    if (listRef.current && pinnedToBottom.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    if (onGrow) onGrow();
  }, [rowCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const textSecondary = colors.textSecondary || 'text.secondary';
  const textPrimary = colors.textPrimary || 'text.primary';
  const elapsed = turn?.startedAt ? Math.max(0, Math.round((now - turn.startedAt) / 1000)) : 0;
  const running = deepestRunning(steps);
  // the steps below say what is running; the headline only says Atom is at it
  let headline = steps.length ? (running ? 'Working' : 'Looking at the results') : 'Working out what to do';
  if (turn?.reconnecting) headline = 'Still working';

  const isOpen = (step) => (open[step.id] !== undefined ? open[step.id] : (step.failedCount || 0) > 0);
  const toggle = (step) => setOpen((prev) => ({ ...prev, [step.id]: !isOpen(step) }));

  const renderStep = (step, depth) => {
    const kids = step.children || [];
    const expandable = kids.length > 0;
    const expanded = expandable && isOpen(step);
    const deepKid = !expanded ? deepestRunning(kids) : null;
    // a Command named like the tool that ran it adds nothing on the collapsed line
    const runningKid = deepKid && (deepKid.text !== step.text || progressText(deepKid)) ? deepKid : null;
    return (
      <Box key={step.id}>
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25, pl: depth * 2, cursor: expandable ? 'pointer' : 'default' }}
          onClick={expandable ? () => toggle(step) : undefined}
          data-testid={`chat-turn-step-${step.kind}`}
        >
          <Box sx={{ width: 16, flexShrink: 0, pt: '2px', display: 'flex', justifyContent: 'center' }}>
            <StateIcon state={step.state} accent={accent} muted={textSecondary} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" sx={{ color: step.state === 'running' ? textPrimary : textSecondary, lineHeight: 1.5 }}>
              {step.text}
              {step.name && step.name !== step.text && (
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', ml: 0.75, opacity: 0.7 }}>{step.name}</Box>
              )}
              {progressText(step) && <Box component="span" sx={{ ml: 0.75 }}>{progressText(step)}</Box>}
              {step.seconds != null && step.state !== 'running' && step.state !== 'note' && (
                <Box component="span" sx={{ ml: 0.75, opacity: 0.7 }}>{formatSeconds(step.seconds)}</Box>
              )}
              {expandable && (
                <Box component="span" sx={{ ml: 0.75, display: 'inline-flex', alignItems: 'center', color: step.failedCount ? FAILED_RED : textSecondary }}>
                  {expanded ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ChevronRightIcon sx={{ fontSize: 14 }} />}
                  {childSummary(step)}
                </Box>
              )}
            </Typography>
            {step.error && (step.state === 'failed' || step.state === 'held') && (
              <Typography variant="caption" sx={{ display: 'block', color: step.state === 'failed' ? FAILED_RED : textSecondary, lineHeight: 1.4 }}>
                {step.error}
              </Typography>
            )}
            {runningKid && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                <CircularProgress size={10} sx={{ color: accent }} />
                <Typography variant="caption" sx={{ color: textSecondary, lineHeight: 1.4 }}>
                  {runningKid.text}{progressText(runningKid) ? ` ${progressText(runningKid)}` : ''}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {expanded && kids.map((kid) => renderStep(kid, depth + 1))}
      </Box>
    );
  };

  return (
    <Box data-testid="chat-turn-activity" sx={{ minWidth: compact ? 0 : 280, maxWidth: 600 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={compact ? 14 : 18} sx={{ color: accent }} />
        <Typography variant="body2" sx={{ color: textSecondary }}>
          {headline}…{elapsed >= 5 ? ` ${formatSeconds(elapsed)}` : ''}
        </Typography>
      </Box>
      {steps.length > 0 && (
        <Box
          ref={listRef}
          data-testid="chat-turn-steps"
          onScroll={(e) => {
            const el = e.currentTarget;
            pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          }}
          sx={{ mt: 1, maxHeight: compact ? 140 : 220, overflowY: 'auto', pr: 1 }}
        >
          {steps.map((step) => renderStep(step, 0))}
        </Box>
      )}
      {turn?.reconnecting ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: textSecondary }}>
          The connection timed out, but Atom is still working on this. The answer will appear here when it is done.
        </Typography>
      ) : elapsed >= STILL_WORKING_SECONDS && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: textSecondary }}>
          This is a big one and can take a few minutes. Keep this open: the answer will appear here.
        </Typography>
      )}
    </Box>
  );
};

const StateIcon = ({ state, accent, muted }) => {
  if (state === 'running') return <CircularProgress size={12} sx={{ color: accent }} />;
  if (state === 'done') return <CheckCircleOutlineIcon sx={{ fontSize: 15, color: accent }} />;
  if (state === 'failed') return <ErrorOutlineIcon sx={{ fontSize: 15, color: FAILED_RED }} />;
  if (state === 'held') return <PauseCircleOutlineIcon sx={{ fontSize: 15, color: muted }} />;
  if (state === 'stopped') return <RemoveCircleOutlineIcon sx={{ fontSize: 15, color: muted }} />;
  return <Box sx={{ width: 5, height: 5, mt: '6px', borderRadius: '50%', bgcolor: muted }} />;
};

// the running row deepest down: what Atom is doing right now
const deepestRunning = (steps) => {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const step = steps[i];
    if (step.state === 'running') return deepestRunning(step.children || []) || step;
  }
  return null;
};

const countRows = (steps) => steps.reduce((n, s) => n + 1 + countRows(s.children || []), 0);

const progressText = (step) => {
  const p = step.progress;
  if (!p) return '';
  const counts = p.total ? `${p.current ?? 0} of ${p.total}` : '';
  if (p.text && counts) return `${p.text} (${counts})`;
  return p.text || counts;
};

const childSummary = (step) => {
  const commands = step.commandCount || 0;
  const failed = step.failedCount || 0;
  if (failed) return `${failed} of ${commands} failed`;
  if (commands) return `${commands} Command${commands === 1 ? '' : 's'}`;
  return `${(step.children || []).length} more`;
};

const formatSeconds = (seconds) => {
  const s = Math.round(seconds);
  if (seconds < 10 && seconds % 1 !== 0) return `${seconds}s`;
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

export default ChatTurnActivity;
